<template>
  <div
    class="flex flex-col overflow-hidden rounded-xl border border-grey-300 shadow-lg lg:flex-row lg:items-stretch"
  >
    <div
      class="flex w-full flex-col justify-between bg-green-800 p-6 md:p-8 lg:w-1/3 lg:p-12"
      :class="dark ? 'bg-jag-950' : 'bg-green-800'"
    >
      <div>
        <div class="tag !text-lemon-200" v-html="heading"></div>
        <h2
          class="h3"
          :class="dark ? 'text-lemon-200' : 'text-white'"
          v-html="title"
        ></h2>
        <p class="!mb-6 text-white md:!mb-8" v-html="desc"></p>
      </div>
      <p class="text-sm font-light text-white">
        Your privacy is important to us. Please review our
        <NuxtLink
          to="https://assets.aussiebroadband.com.au/web/assets/legal/policies/abb-privacy-policy.pdf"
          class="text-inherit"
          :external="true"
          target="_blank"
          >privacy policy
        </NuxtLink>
        to see how we protect your information.
      </p>
    </div>
    <div class="w-full bg-white p-6 md:p-8 lg:w-2/3 lg:px-16 lg:py-12">
      <div v-if="!submitted">
        <form noValidate @submit.prevent="handleSubmit">
          <h3 class="!mb-6 md:!mb-8">Speak to an expert</h3>

          <div class="grid w-full grid-cols-2 gap-6 md:gap-8">
            <div class="col-span-2 md:col-span-1">
              <InputBase
                v-model="formData.first_name"
                label="First Name"
                :error="formErrors.first_name"
                type="text"
                :icon="IconPerson"
              />
            </div>

            <div class="col-span-2 md:col-span-1">
              <InputBase
                v-model="formData.last_name"
                label="Last Name"
                :error="formErrors.last_name"
                type="text"
                :icon="IconPerson"
              />
            </div>

            <div class="col-span-2">
              <InputBase
                v-model="formData.phone_number"
                label="Phone"
                :error="formErrors.phone_number"
                type="tel"
              />
            </div>

            <div class="col-span-2">
              <InputBase
                v-model="formData.email_address"
                label="Email"
                :error="formErrors.email_address"
                type="email"
              />
            </div>

            <div class="col-span-2">
              <InputBase
                v-model="formData.business_name"
                label="Company Name"
                :error="formErrors.business_name"
                type="text"
                :icon="IconBuilding"
              />
            </div>
            <div class="col-span-2">
              <InputTextareaBase
                id="form_comments"
                v-model="formData.notes"
                label="How can we assist you?"
              />
            </div>
          </div>
          <Alert
            v-if="error"
            type="fatal"
            :content="{ body: error, isVhtml: true }"
            size="small"
            class="mt-6 md:mt-8"
          />
          <span class="flex justify-end">
            <BtnPrimary
              label="Submit"
              size="large"
              arrow="right"
              type="submit"
              :loading="loading"
              class="mt-6 !w-60 md:mt-8"
            />
          </span>
        </form>
      </div>
      <div v-else class="py-32">
        <div class="tag">Submission complete</div>
        <h3>Thanks for contacting Aussie broadband.</h3>
        <p>
          One of our team will contact you to discuss your enquiry. If you have
          an urgent customer service issue, we suggest calling our
          Australian-based support team on
          <NuxtLink :to="businessPhone.sales.url">{{
            businessPhone.sales.text
          }}</NuxtLink
          >.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LeadMethod, type GenericLead } from "~/types/genericLead";
import IconPerson from "@/components/icon/Person.vue";
import IconBuilding from "@/components/icon/Building.vue";

const props = defineProps<{
  heading: string;
  title: string;
  desc: string;
  dark?: boolean;
  leadSource?: LeadMethod; // e.g. "Managed Services Form"
  propertyType?: GenericLead["property_type"];
}>();

const { businessPhone } = getPhoneNumbers();
const formData = reactive({
  property_type: props.propertyType || "business",
  lead_source: props.leadSource || LeadMethod.BUSINESS_QUOTE,
  first_name: "",
  last_name: "",
  phone_number: "",
  email_address: "",
  business_name: "",
  // interests: [],
  // number_of_employees: "",
  notes: "",
});

const formErrors = reactive({
  first_name: "",
  last_name: "",
  phone_number: "",
  email_address: "",
  business_name: "",
});

const clearErrors = () => {
  formErrors.first_name = "";
  formErrors.last_name = "";
  formErrors.phone_number = "";
  formErrors.email_address = "";
  formErrors.business_name = "";
};

const loading = ref(false);
const submitted = ref(false);
const error = ref("");

const handleSubmit = async () => {
  loading.value = true;
  error.value = "";
  clearErrors();

  formErrors.first_name = validateRequired(
    formData.first_name,
    "First name is required",
  );
  formErrors.last_name = validateRequired(
    formData.last_name,
    "Last name is required",
  );
  formErrors.phone_number = validatePhone(formData.phone_number, {
    required: true,
  });
  formErrors.email_address = validateEmail(formData.email_address, {
    required: true,
  });
  formErrors.business_name = validateRequired(
    formData.business_name,
    "Company name is required",
  );

  if (Object.values(formErrors).some((err) => err.length > 0)) {
    loading.value = false;
    return;
  }

  if (formData.notes.length === 0) formData.notes = "no";

  createGenericLead(formData as GenericLead)
    .then(() => {
      submitted.value = true;
    })
    .catch((e) => {
      error.value =
        e.message ?? "Something went wrong. Please try again later.";
    })
    .finally(() => {
      loading.value = false;
    });
};
</script>
